import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import AdminSidebar from '../../components/AdminSidebar';
import DiscussionFilters from './components/DiscussionFilters';
import DiscussionTable from './components/DiscussionTable';
import DiscussionDetailPanel from './components/DiscussionDetailPanel';
import { BookOpen } from 'lucide-react';

const AdminDiscussionsModerationDashboard = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('sve');
  const [statusFilter, setStatusFilter] = useState('svi');
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  const fetchTopics = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let query = supabase?.from('discussion_topics')?.select(`
          id, title, body, created_at, is_active, is_visible, moderator_note, user_id,
          author:user_profiles!discussion_topics_user_id_fkey(id, full_name, email),
          discussion_comments(count)
        `)?.order('created_at', { ascending: false });

      if (statusFilter === 'aktivni') {
        query = query?.eq('is_active', true)?.eq('is_visible', true);
      } else if (statusFilter === 'skriveni') {
        query = query?.or('is_active.eq.false,is_visible.eq.false');
      }

      const { data, error: fetchError } = await query;
      if (fetchError) throw fetchError;

      const mapped = (data || [])?.map((t) => ({
        id: t?.id,
        title: t?.title,
        body: t?.body,
        createdAt: t?.created_at,
        isActive: t?.is_active ?? true,
        isVisible: t?.is_visible ?? true,
        moderatorNote: t?.moderator_note,
        userId: t?.user_id,
        authorName: t?.author?.full_name || t?.author?.email || 'Korisnik',
        commentCount: t?.discussion_comments?.[0]?.count || 0,
      }));

      setTopics(mapped);
    } catch (err) {
      setError(err?.message || 'Greška pri učitavanju diskusija');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchTopics();
  }, [fetchTopics]);

  const filteredTopics = topics?.filter((t) => {
    if (!search) return true;
    const q = search?.toLowerCase();
    return (t?.title?.toLowerCase()?.includes(q) ||
    t?.body?.toLowerCase()?.includes(q) || t?.authorName?.toLowerCase()?.includes(q));
  });

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(null), 3000);
  };

  const handleHide = async (id) => {
    setSaving(true);
    try {
      const { error: updateError } = await supabase?.from('discussion_topics')?.update({ is_active: false, is_visible: false, moderated_at: new Date()?.toISOString() })?.eq('id', id);
      if (updateError) throw updateError;
      await fetchTopics();
      setSelectedTopic(null);
      showSuccess('Diskusija je skrivena');
    } catch (err) {
      setError(err?.message);
    } finally {
      setSaving(false);
    }
  };

  const handleRestore = async (id) => {
    setSaving(true);
    try {
      const { error: updateError } = await supabase?.from('discussion_topics')?.update({ is_active: true, is_visible: true })?.eq('id', id);
      if (updateError) throw updateError;
      await fetchTopics();
      setSelectedTopic(null);
      showSuccess('Diskusija je vraćena');
    } catch (err) {
      setError(err?.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-100">
      <AdminSidebar isCollapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
      <div className={`flex-1 transition-all duration-300 ${sidebarCollapsed ? 'ml-20' : 'ml-64'}`}>
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Moderacija Diskusija</h1>
              <p className="text-sm text-gray-500">Pregled i upravljanje diskusijama i savetima na platformi</p>
            </div>
          </div>

          {/* Alerts */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
              <button onClick={() => setError(null)} className="ml-2 font-medium underline">Zatvori</button>
            </div>
          )}
          {successMsg && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
              {successMsg}
            </div>
          )}

          {/* Main content */}
          <div className="flex gap-6">
            {/* Left: Table */}
            <div className="flex-1 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-4 border-b border-gray-200">
                <DiscussionFilters
                  search={search}
                  onSearchChange={setSearch}
                  typeFilter={typeFilter}
                  onTypeChange={setTypeFilter}
                  statusFilter={statusFilter}
                  onStatusChange={setStatusFilter}
                />
                <p className="text-xs text-gray-500">{filteredTopics?.length} diskusija</p>
              </div>
              <DiscussionTable
                topics={filteredTopics}
                selectedId={selectedTopic?.id}
                onSelect={setSelectedTopic}
                loading={loading}
              />
            </div>

            {/* Right: Detail panel */}
            {selectedTopic && (
              <div className="w-96 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex-shrink-0">
                <DiscussionDetailPanel
                  topic={selectedTopic}
                  onClose={() => setSelectedTopic(null)}
                  onHide={handleHide}
                  onRestore={handleRestore}
                  saving={saving}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDiscussionsModerationDashboard;
